import { NavLink } from 'react-router-dom';
import { useState, type ReactNode } from 'react';
import {
  LayoutDashboard,
  FolderKanban,
  Target,
  Calendar,
  Users,
  AlertTriangle,
  GitBranch,
  FileText,
  BarChart3,
  Settings,
  ChevronDown,
  Clock,
  Inbox,
  BookOpen,
  Boxes,
  X,
  PanelLeftClose,
  PanelLeftOpen,
  Calculator,
  Compass,
  Sparkles,
} from 'lucide-react';
import { cn } from '@/lib/cn';
import { useCanManage } from '@/hooks/useCurrentRole';
import { useMobileNavStore } from '@/store/useMobileNavStore';
import { useSidebarStore } from '@/store/useSidebarStore';

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  badge?: string | number;
  disabled?: boolean;
}

interface NavSection {
  title: string;
  items: NavItem[];
  defaultOpen?: boolean;
}

interface NavData {
  /** Ungrouped items pinned above the first section (Home). */
  top: NavItem[];
  /** Collapsible, role-ordered sections. */
  sections: NavSection[];
  /** Ungrouped items pinned to the bottom, visually separated (Guide, Settings). */
  footer: NavItem[];
}

/** Build the nav (role-aware). Shared by the desktop sidebar and the mobile
 *  drawer so the two never drift apart.
 *
 *  Order for managers: Delivery → Planning → Governance → Insights → My Work.
 *  Contributors (no manage rights) live in My Work, so it floats to the top for
 *  them. Home is always pinned above; User Guide + Settings sit in a separated
 *  footer group pinned to the bottom. */
function useNavSections(): NavData {
  const canManage = useCanManage();

  // "My Work" — personal, user-centric execution views. Team Time is a
  // manager-only roll-up so it's gated to Owner/Admin.
  const myWorkItems: NavItem[] = [
    { to: '/my-tasks', label: 'My Tasks', icon: <Calendar className="w-4 h-4" /> },
    { to: '/timesheet', label: 'My Timesheet', icon: <Clock className="w-4 h-4" /> },
  ];
  if (canManage) {
    myWorkItems.push({
      to: '/team-time',
      label: 'Team Time',
      icon: <Users className="w-4 h-4" />,
    });
  }

  const delivery: NavSection = {
    // Demand → delivery, in drill-down order: the pipeline's front door
    // (Intake) leads into the Portfolios → Programs → Projects hierarchy.
    title: 'Delivery',
    defaultOpen: true,
    items: [
      { to: '/intake', label: 'Intake & Demand', icon: <Inbox className="w-4 h-4" /> },
      { to: '/portfolios', label: 'Portfolios', icon: <Target className="w-4 h-4" /> },
      { to: '/programs', label: 'Programs', icon: <Boxes className="w-4 h-4" /> },
      { to: '/projects', label: 'Projects', icon: <FolderKanban className="w-4 h-4" /> },
    ],
  };
  const planning: NavSection = {
    // Shared cross-project planning tools (not personal, not a hierarchy tier).
    title: 'Planning',
    defaultOpen: true,
    items: [
      { to: '/estimator', label: 'Estimator', icon: <Calculator className="w-4 h-4" /> },
      { to: '/methodology', label: 'Methodology Advisor', icon: <Compass className="w-4 h-4" /> },
      { to: '/schedule', label: 'Schedule', icon: <Calendar className="w-4 h-4" /> },
      { to: '/resources', label: 'Capacity & Resources', icon: <Users className="w-4 h-4" /> },
    ],
  };
  const governance: NavSection = {
    // Executive Briefing folded into the Reports hub (Reports is the single door
    // to every report), so Insights collapsed into Governance rather than
    // leaving a one-item section with a header.
    title: 'Governance',
    defaultOpen: true,
    items: [
      { to: '/risks', label: 'Risk Register', icon: <AlertTriangle className="w-4 h-4" /> },
      { to: '/changes', label: 'Change Log', icon: <GitBranch className="w-4 h-4" /> },
      { to: '/documents', label: 'Document Library', icon: <FileText className="w-4 h-4" /> },
      { to: '/lessons', label: 'Lessons-Learned', icon: <BookOpen className="w-4 h-4" /> },
      { to: '/reports', label: 'Reports', icon: <BarChart3 className="w-4 h-4" /> },
    ],
  };
  const myWork: NavSection = {
    title: 'My Work',
    defaultOpen: true,
    items: myWorkItems,
  };

  const sections = canManage
    ? [delivery, planning, governance, myWork]
    : [myWork, delivery, planning, governance];

  return {
    top: [{ to: '/', label: 'Home', icon: <LayoutDashboard className="w-4 h-4" /> }],
    sections,
    footer: [
      { to: '/whats-new', label: "What's New", icon: <Sparkles className="w-4 h-4" /> },
      { to: '/guide', label: 'User Guide', icon: <BookOpen className="w-4 h-4" /> },
      { to: '/settings', label: 'Settings', icon: <Settings className="w-4 h-4" /> },
    ],
  };
}

function SidebarSections({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const { top, sections, footer } = useNavSections();
  return (
    <div className="flex flex-col min-h-full">
      {/* Home — pinned above the first section, no section header. */}
      <div className="space-y-px mb-1">
        {top.map((item) => (
          <SidebarItem key={item.to} item={item} onNavigate={onNavigate} collapsed={collapsed} />
        ))}
      </div>
      {sections.map((section) => (
        <NavSection
          key={section.title}
          section={section}
          onNavigate={onNavigate}
          collapsed={collapsed}
        />
      ))}
      {/* Footer group — pinned to the bottom, separated from the sections. */}
      <div className="mt-auto pt-3 border-t border-line space-y-px">
        {footer.map((item) => (
          <SidebarItem key={item.to} item={item} onNavigate={onNavigate} collapsed={collapsed} />
        ))}
      </div>
    </div>
  );
}

/** Persistent sidebar for tablet/desktop widths. Hidden on phones, where the
 *  MobileNavDrawer takes over. Collapses to an icon-only rail (persisted). */
export function Sidebar() {
  const collapsed = useSidebarStore((s) => s.collapsed);
  const toggle = useSidebarStore((s) => s.toggle);
  return (
    <nav
      className={cn(
        'hidden lg:flex flex-col bg-white border-r border-line h-full transition-[width] duration-200',
        collapsed ? 'w-14' : 'w-60',
      )}
    >
      <div className={cn('flex-1 overflow-y-auto overflow-x-hidden', collapsed ? 'p-2' : 'p-3')}>
        <SidebarSections collapsed={collapsed} />
      </div>
      <button
        type="button"
        onClick={toggle}
        title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        className={cn(
          'flex items-center gap-2 border-t border-line px-3 py-2.5 text-sm text-ink-tertiary hover:text-ink-primary hover:bg-surface-muted transition-colors',
          collapsed && 'justify-center px-0',
        )}
      >
        {collapsed ? (
          <PanelLeftOpen className="w-4 h-4" />
        ) : (
          <>
            <PanelLeftClose className="w-4 h-4" />
            <span>Collapse</span>
          </>
        )}
      </button>
    </nav>
  );
}

/** Slide-in navigation drawer for phone widths (hidden on lg+). Opened from the
 *  header hamburger; closes on backdrop tap or when a destination is chosen. */
export function MobileNavDrawer() {
  const isOpen = useMobileNavStore((s) => s.isOpen);
  const close = useMobileNavStore((s) => s.close);

  return (
    <div
      className={cn('lg:hidden fixed inset-0 z-[200]', !isOpen && 'pointer-events-none')}
      aria-hidden={!isOpen}
    >
      <div
        className={cn(
          'absolute inset-0 bg-black/40 transition-opacity duration-200',
          isOpen ? 'opacity-100' : 'opacity-0',
        )}
        onClick={close}
      />
      <nav
        className={cn(
          'absolute inset-y-0 left-0 w-72 max-w-[80%] bg-white border-r border-line',
          'p-3 pt-safe-top overflow-y-auto transition-transform duration-200 ease-out',
          isOpen ? 'translate-x-0' : '-translate-x-full',
        )}
      >
        <div className="flex justify-end mb-1">
          <button
            type="button"
            onClick={close}
            aria-label="Close menu"
            className="p-2 text-ink-tertiary hover:text-ink-primary"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <SidebarSections onNavigate={close} />
      </nav>
    </div>
  );
}

function NavSection({
  section,
  onNavigate,
  collapsed = false,
}: {
  section: NavSection;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  const [open, setOpen] = useState(section.defaultOpen ?? true);

  // Collapsed rail: no headers (there's no room), items always shown as icons,
  // with a hairline divider between groups so the structure still reads.
  if (collapsed) {
    return (
      <div className="mb-1 pt-1 first:pt-0 border-t first:border-t-0 border-line-light space-y-px">
        {section.items.map((item) => (
          <SidebarItem key={item.to} item={item} onNavigate={onNavigate} collapsed />
        ))}
      </div>
    );
  }

  return (
    <div className="mb-1">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider text-ink-quaternary hover:text-ink-tertiary transition-colors"
      >
        <span>{section.title}</span>
        <ChevronDown
          className={cn(
            'w-3 h-3 transition-transform',
            open ? 'rotate-0' : '-rotate-90',
          )}
        />
      </button>
      {open && (
        <div className="space-y-px">
          {section.items.map((item) => (
            <SidebarItem key={item.to} item={item} onNavigate={onNavigate} />
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarItem({
  item,
  onNavigate,
  collapsed = false,
}: {
  item: NavItem;
  onNavigate?: () => void;
  collapsed?: boolean;
}) {
  if (item.disabled) {
    return (
      <div
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded text-sm text-ink-quaternary cursor-not-allowed',
          collapsed && 'justify-center px-0',
        )}
        title={collapsed ? `${item.label} — coming in a later phase` : 'Coming in a later phase'}
      >
        {item.icon}
        {!collapsed && <span>{item.label}</span>}
        {!collapsed && (
          <span className="ml-auto text-[10px] uppercase tracking-wider text-ink-quaternary">
            soon
          </span>
        )}
      </div>
    );
  }

  return (
    <NavLink
      to={item.to}
      end={item.to === '/'}
      onClick={onNavigate}
      title={collapsed ? item.label : undefined}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-2 px-3 py-2 rounded text-sm font-medium transition-colors',
          'border-l-[3px] -ml-[3px] pl-[calc(0.75rem-3px+3px)]',
          collapsed && 'justify-center px-0 pl-0 ml-0',
          isActive
            ? 'text-brand-navy border-brand-orange font-semibold'
            : 'text-ink-secondary border-transparent hover:bg-surface-muted hover:text-ink-primary',
        )
      }
    >
      {item.icon}
      {!collapsed && <span>{item.label}</span>}
      {item.badge != null &&
        (collapsed ? (
          <span className="absolute top-1 right-1 w-1.5 h-1.5 rounded-full bg-brand-orange" />
        ) : (
          <span className="ml-auto bg-brand-orange text-white text-xs font-bold px-2 rounded-full">
            {item.badge}
          </span>
        ))}
    </NavLink>
  );
}
